<template>
  <div class="home">
    <!-- Hero Section -->
    <section class="hero">
      <div class="container">
        <div class="hero-content">
          <div class="hero-left">
            <h1 class="hero-title">
              {{ $t('home.title').split('，')[0] }}，<span class="gradient-text">{{ $t('home.title').split('，')[1] || $t('home.title') }}</span>
            </h1>
            <p class="hero-subtitle">
              {{ $t('home.subtitle') }}
            </p>

            <div class="hero-actions">
              <button class="btn btn-primary" @click="scrollToDemo">{{ $t('home.quickStart') }}</button>
              <button class="btn btn-secondary">{{ $t('home.pricing') }}</button>
            </div>

            <!-- Stats -->
            <div class="stats">
              <div class="stat-item">
                <div class="stat-number">1M+</div>
                <div class="stat-label">{{ $t('home.stats.accelerations') }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">50K+</div>
                <div class="stat-label">{{ $t('home.stats.users') }}</div>
              </div>
              <div class="stat-item">
                <div class="stat-number">99.9%</div>
                <div class="stat-label">{{ $t('home.stats.uptime') }}</div>
              </div>
            </div>
          </div>

          <div class="hero-right">
            <div class="hero-image">
              <div class="docker-container">
                <div class="container-header">
                  <div class="container-controls">
                    <div class="control-dot red"></div>
                    <div class="control-dot yellow"></div>
                    <div class="control-dot green"></div>
                  </div>
                  <span class="container-title">docker pull nginx:latest</span>
                </div>
                <div class="container-content">
                  <div class="terminal-line">
                    <span class="prompt">$</span>
                    <span class="command">docker pull docker1s.com/nginx:latest</span>
                  </div>
                  <div class="terminal-line">
                    <span class="output">latest: Pulling from nginx</span>
                  </div>
                  <div class="terminal-line">
                    <span class="output">✓ Downloaded 5MB/5MB [====================] 100%</span>
                  </div>
                  <div class="terminal-line">
                    <span class="output">✓ Successfully pulled nginx:latest</span>
                  </div>
                  <div class="terminal-line">
                    <span class="output success">🚀 Accelerated by Docker1s - 10x faster!</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Demo Section -->
    <section id="demo" class="demo-section section">
      <div class="container">
        <h2 class="section-title">{{ $t('home.demo.title') }}</h2>
        <p class="section-subtitle">{{ $t('home.demo.subtitle') }}</p>

        <div class="demo-container">
          <div class="demo-tabs">
            <button
              v-for="(tab, index) in demoTabs"
              :key="index"
              :class="['tab-button', { active: activeTab === index }]"
              @click="activeTab = index"
            >
              {{ tab.name }}
            </button>
          </div>

          <div class="demo-content">
            <div class="code-block">
              <div class="code-header">
                <span class="code-title">{{ demoTabs[activeTab].title }}</span>
                <button class="copy-button" @click="copyCommand">{{ $t('common.copy') }}</button>
              </div>
              <pre class="code-content">{{ demoTabs[activeTab].command }}</pre>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="features section">
      <div class="container">
        <h2 class="section-title">{{ $t('home.features.title') }}</h2>
        <p class="section-subtitle">{{ $t('home.features.subtitle') }}</p>

        <div class="features-grid">
          <div class="feature-card" v-for="feature in features" :key="feature.id">
            <div class="feature-icon">
              <component :is="feature.icon" />
            </div>
            <h3 class="feature-title">{{ feature.title }}</h3>
            <p class="feature-desc">{{ feature.description }}</p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'Home',
  data() {
    return {
      activeTab: 0
    }
  },
  computed: {
    demoTabs() {
      return [
        {
          name: this.$t('home.demo.tabs.config'),
          title: 'Configure Docker Mirror',
          command: `# Configure Docker daemon
sudo mkdir -p /etc/docker
sudo tee /etc/docker/daemon.json <<-'EOF'
{
  "registry-mirrors": ["https://docker1s.com"]
}
EOF
sudo systemctl daemon-reload
sudo systemctl restart docker`
        },
        {
          name: this.$t('home.demo.tabs.pull'),
          title: 'Pull Accelerated Images',
          command: `# Pull common images
docker pull docker1s.com/nginx:latest
docker pull docker1s.com/redis:alpine
docker pull docker1s.com/mysql:8.0
docker pull docker1s.com/node:18-alpine`
        },
        {
          name: this.$t('home.demo.tabs.k8s'),
          title: 'Kubernetes Deployment',
          command: `# Use accelerated images in K8s
apiVersion: apps/v1
kind: Deployment
metadata:
  name: nginx-deployment
spec:
  template:
    spec:
      containers:
      - name: nginx
        image: docker1s.com/nginx:latest`
        },
        {
          name: this.$t('home.demo.tabs.compose'),
          title: 'Docker Compose Config',
          command: `# docker-compose.yml
version: '3.8'
services:
  web:
    image: docker1s.com/nginx:latest
    ports:
      - "80:80"
  db:
    image: docker1s.com/mysql:8.0
    environment:
      MYSQL_ROOT_PASSWORD: example`
        }
      ]
    },
    features() {
      return [
        {
          id: 1,
          icon: 'SpeedIcon',
          title: this.$t('home.features.items.speed.title'),
          description: this.$t('home.features.items.speed.desc')
        },
        {
          id: 2,
          icon: 'GlobalIcon',
          title: this.$t('home.features.items.global.title'),
          description: this.$t('home.features.items.global.desc')
        },
        {
          id: 3,
          icon: 'SecureIcon',
          title: this.$t('home.features.items.secure.title'),
          description: this.$t('home.features.items.secure.desc')
        },
        {
          id: 4,
          icon: 'DownloadIcon',
          title: this.$t('home.features.items.free.title'),
          description: this.$t('home.features.items.free.desc')
        },
        {
          id: 5,
          icon: 'IntelligentIcon',
          title: this.$t('home.features.items.intelligent.title'),
          description: this.$t('home.features.items.intelligent.desc')
        },
        {
          id: 6,
          icon: 'SupportIcon',
          title: this.$t('home.features.items.support.title'),
          description: this.$t('home.features.items.support.desc')
        }
      ]
    }
  },
  methods: {
    scrollToDemo() {
      document.getElementById('demo').scrollIntoView({ behavior: 'smooth' })
    },
    copyCommand() {
      const command = this.demoTabs[this.activeTab].command
      navigator.clipboard.writeText(command).then(() => {
        // 这里可以添加复制成功的提示
        console.log('Command copied to clipboard')
      })
    }
  },
  components: {
    SpeedIcon: {
      template: `
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L13.09 8.26L19 7L16.74 12.74L23 14L16.74 15.26L19 21L13.09 19.74L12 26L10.91 19.74L5 21L7.26 15.26L1 14L7.26 12.74L5 7L10.91 8.26L12 2Z" fill="#8b5cf6"/>
        </svg>
      `
    },
    GlobalIcon: {
      template: `
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="10" stroke="#3b82f6" stroke-width="2"/>
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" stroke="#3b82f6" stroke-width="2"/>
        </svg>
      `
    },
    SecureIcon: {
      template: `
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="#10b981" stroke-width="2" fill="#10b981" fill-opacity="0.1"/>
          <path d="M9 12l2 2 4-4" stroke="#10b981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
    },
    DownloadIcon: {
      template: `
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="#f97316" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      `
    },
    IntelligentIcon: {
      template: `
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 3C16.97 3 21 7.03 21 12s-4.03 9-9 9c-1.24 0-2.43-.2-3.53-.57L3 21l1.57-5.47C4.2 14.43 4 13.24 4 12c0-4.97 4.03-9 9-9z" stroke="#6366f1" stroke-width="2"/>
          <circle cx="9" cy="10" r="1" fill="#6366f1"/>
          <circle cx="15" cy="10" r="1" fill="#6366f1"/>
          <path d="M9 14h6" stroke="#6366f1" stroke-width="2" stroke-linecap="round"/>
        </svg>
      `
    },
    SupportIcon: {
      template: `
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" stroke="#059669" stroke-width="2"/>
          <path d="M13 8H8M16 12H8" stroke="#059669" stroke-width="2" stroke-linecap="round"/>
          <circle cx="19" cy="6" r="3" fill="#dc2626"/>
          <path d="M19 4v4M17 6h4" stroke="white" stroke-width="1" stroke-linecap="round"/>
        </svg>
      `
    }
  }
}
</script>

<style scoped>
.hero {
  padding: 6rem 0 4rem;
  background: linear-gradient(135deg, #f8faff 0%, #f0f4ff 100%);
  position: relative;
  overflow: hidden;
}

.hero::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 20%, rgba(102, 126, 234, 0.1) 0%, transparent 50%);
}

.hero-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 1;
}

.hero-title {
  font-size: 3.5rem;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 1.5rem;
  color: #1a1a1a;
}

.hero-subtitle {
  font-size: 1.25rem;
  color: #666;
  margin-bottom: 2rem;
  line-height: 1.6;
}

.hero-actions {
  display: flex;
  gap: 1rem;
  margin-bottom: 3rem;
}

.stats {
  display: flex;
  gap: 2rem;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: #667eea;
}

.stat-label {
  font-size: 0.875rem;
  color: #666;
  margin-top: 0.25rem;
}

.hero-right {
  display: flex;
  justify-content: center;
  align-items: center;
}

.docker-container {
  background: #1a1a1a;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  width: 100%;
  max-width: 500px;
}

.container-header {
  background: #2d2d2d;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.container-controls {
  display: flex;
  gap: 6px;
}

.control-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

.control-dot.red { background: #ff5f56; }
.control-dot.yellow { background: #ffbd2e; }
.control-dot.green { background: #27ca3f; }

.container-title {
  color: #ccc;
  font-size: 14px;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
}

.container-content {
  padding: 20px;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  font-size: 14px;
  line-height: 1.6;
}

.terminal-line {
  margin-bottom: 8px;
  color: #ccc;
}

.prompt {
  color: #667eea;
  margin-right: 8px;
}

.command {
  color: #fff;
}

.output {
  color: #a0a0a0;
}

.output.success {
  color: #27ca3f;
}

.demo-section {
  background: white;
}

.demo-container {
  max-width: 800px;
  margin: 0 auto;
}

.demo-tabs {
  display: flex;
  background: #f8f9fa;
  border-radius: 12px;
  padding: 4px;
  margin-bottom: 2rem;
  overflow-x: auto;
}

.tab-button {
  flex: 1;
  padding: 12px 16px;
  border: none;
  background: transparent;
  color: #666;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  min-width: 120px;
}

.tab-button.active {
  background: white;
  color: #667eea;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.code-block {
  background: #1a1a1a;
  border-radius: 12px;
  overflow: hidden;
}

.code-header {
  background: #2d2d2d;
  padding: 12px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.code-title {
  color: #ccc;
  font-weight: 500;
}

.copy-button {
  padding: 6px 12px;
  background: #667eea;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.3s ease;
}

.copy-button:hover {
  background: #5a67d8;
}

.code-content {
  padding: 20px;
  color: #ccc;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  font-size: 14px;
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre-wrap;
}

.features {
  background: #fafbff;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
}

.feature-card {
  background: white;
  padding: 2rem;
  border-radius: 16px;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.feature-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
}

.feature-icon {
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: center;
}

.feature-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #1a1a1a;
}

.feature-desc {
  color: #666;
  line-height: 1.6;
}

@media (max-width: 768px) {
  .hero-content {
    grid-template-columns: 1fr;
    gap: 2rem;
    text-align: center;
  }

  .hero-title {
    font-size: 2.5rem;
  }

  .hero-actions {
    justify-content: center;
  }

  .stats {
    justify-content: center;
  }

  .demo-tabs {
    flex-direction: column;
  }

  .tab-button {
    min-width: auto;
  }
}
</style>
